const KL_GIFT_WRAP_SELECTOR = '[data-kl-gift-wrap]';
const KL_GIFT_WRAP_TOGGLE_SELECTOR = '[data-kl-gift-wrap-toggle]';
const KL_GIFT_WRAP_SUBMIT_SELECTOR = '[type="submit"][name="add"]';
const KL_GIFT_WRAP_PARENT_PROPERTY = '_kl_gift_wrap_for';

/*
 * registerGate, unregisterGate, hasGateCleared, markGateCleared and
 * clearGateStateOnRealSubmit come from assets/kl-form-gate.js, loaded first —
 * see that file for what the 'gift-wrap' gate name below is coordinating with.
 */

function buildGiftWrapProperties(propertyName, propertyValue, parentProductId) {
  const properties = { [KL_GIFT_WRAP_PARENT_PROPERTY]: parentProductId || '' };

  if (propertyName) properties[propertyName] = propertyValue || 'Yes';

  return properties;
}

function getGiftWrapQuantity(formQuantity, isMatchingQuantity) {
  if (!isMatchingQuantity) return 1;

  const quantity = Number.parseInt(formQuantity, 10);

  return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
}

function getIsGiftWrapLinePresent(cartItems, variantId, parentProductId) {
  if (!Array.isArray(cartItems)) return false;

  return cartItems.some(
    (item) =>
      String(item?.variant_id) === String(variantId) &&
      String(item?.properties?.[KL_GIFT_WRAP_PARENT_PROPERTY] ?? '') === String(parentProductId)
  );
}

async function fetchCart(cartUrl) {
  const response = await fetch(`${cartUrl}.js`, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) throw new Error(`Cart request failed with ${response.status}`);

  return response.json();
}

async function addGiftWrap(root, form) {
  const {
    klGiftWrapVariant: variantId,
    klGiftWrapParent: parentProductId,
    klGiftWrapAddUrl: addUrl,
    klGiftWrapCartUrl: cartUrl,
    klGiftWrapPropertyName: propertyName,
    klGiftWrapPropertyValue: propertyValue,
    klGiftWrapUnique: uniqueFlag,
    klGiftWrapMatchQuantity: matchQuantityFlag,
  } = root.dataset;

  if (!variantId || !addUrl) throw new Error('Gift packaging is missing its variant or cart route');

  if (uniqueFlag === 'true') {
    const cart = await fetchCart(cartUrl);

    if (getIsGiftWrapLinePresent(cart?.items, variantId, parentProductId)) return;
  }

  const quantity = getGiftWrapQuantity(form.elements.namedItem('quantity')?.value, matchQuantityFlag === 'true');

  const response = await fetch(`${addUrl}.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      items: [
        {
          id: variantId,
          quantity,
          properties: buildGiftWrapProperties(propertyName, propertyValue, parentProductId),
        },
      ],
    }),
  });

  if (!response.ok) throw new Error(`Gift packaging could not be added (${response.status})`);
}

function replaySubmit(form) {
  const submitButton = form.querySelector(KL_GIFT_WRAP_SUBMIT_SELECTOR);

  if (submitButton && !submitButton.disabled) {
    form.requestSubmit(submitButton);
    return;
  }

  form.requestSubmit();
}

function initGiftWrap(root) {
  if (!root || root.dataset.klGiftWrapReady === 'true') return;

  const form = document.getElementById(root.dataset.klGiftWrapForm || '');
  const toggle = root.querySelector(KL_GIFT_WRAP_TOGGLE_SELECTOR);

  if (!form || !toggle) {
    console.debug('kl-gift-wrap: the block could not find its form or its checkbox', {
      formId: root.dataset.klGiftWrapForm,
      hasToggle: Boolean(toggle),
    });
    return;
  }

  root.dataset.klGiftWrapReady = 'true';
  clearGateStateOnRealSubmit(form);

  /*
   * Registered only while the box is checked: an unchecked gift-wrap never
   * intercepts a submit, so it must never count as a pending gate either —
   * see the isPending guard below and hasPendingGate in kl-form-gate.js,
   * which kl-cart-instant-open.js reads before it opens the drawer early.
   */
  const syncGateRegistration = () => {
    if (toggle.checked) {
      registerGate(form, 'gift-wrap');
    } else {
      unregisterGate(form, 'gift-wrap');
    }
  };

  syncGateRegistration();
  toggle.addEventListener('change', syncGateRegistration);

  let isPending = false;

  document.addEventListener(
    'submit',
    (event) => {
      if (event.target !== form || hasGateCleared(form, 'gift-wrap') || !toggle.checked) return;

      event.preventDefault();
      event.stopPropagation();

      if (isPending) return;

      isPending = true;

      addGiftWrap(root, form)
        .catch((error) => {
          console.debug('kl-gift-wrap: the packaging was not added; continuing with the product', {
            variantId: root.dataset.klGiftWrapVariant,
            error,
          });
        })
        .finally(() => {
          isPending = false;
          markGateCleared(form, 'gift-wrap');
          try {
            replaySubmit(form);
          } catch (error) {
            console.debug('kl-gift-wrap: the product form could not be resubmitted', { error });
          }
        });
    },
    true
  );
}

document.querySelectorAll(KL_GIFT_WRAP_SELECTOR).forEach(initGiftWrap);

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll(KL_GIFT_WRAP_SELECTOR).forEach(initGiftWrap);
});
